/**
 * OC2-3562 — <PrescriptionSection> (top-level wrapper)
 *
 */

import RichText from '@/components/RichText'
import { useWebsiteConfigElements } from '@/components/WebsiteConfiguration'
import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { useIframeSide } from '@/hooks/useIframe'
import { useSideBySideSupport } from '@/hooks/useSideBySideSupport'
import type {
	Focusarea,
	Focusareaoption,
	Syllabus,
} from '@/kontent/content-types'
import { OutcomeDetailCardTitle } from '@/legacy-ported/components/card/OutcomeDetailCard'
import type { Mapping } from '@/types'
import { getUrlFromMapping, isRichtextElementEmpty } from '@/utils'
import { isFocusarea } from '@/utils/type_predicates'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useCallback, useEffect, useId, useRef } from 'react'
import PrescriptionGroup from './PrescriptionGroup'
import PrescriptionOption from './PrescriptionOption'
import { PrescriptionInPageAlert } from './PrescriptionStatusAlert'
import type {
	PrescriptionGroup as PrescriptionGroupType,
	PrescriptionsList,
	VariantBranch,
} from './types'
import { dispatchVariant } from './utils/dispatchVariant'
import { hasCollectionsTrigger } from './utils/hasCollectionsTrigger'
import { resolveList } from './utils/resolveList'
import {
	getGAPageContext,
	PRESCRIPTION_GA_EVENT,
} from './utils/prescriptionGAEvents'
import { sendPrescriptionGAEvent } from './utils/sendPrescriptionGAEvent'

// CMS item codename for the Prescription Tools page. Stable across slug
// changes — the actual URL is resolved from the sitemap mappings at render time
// so editorial slug edits flow through without a code change.
const PRESCRIPTION_TOOLS_CODENAME = 'prescription_tools'
const PRESCRIPTION_TOOLS_FALLBACK_PATH = '/prescription-tools'

export interface PrescriptionSectionProps {
	currentFocusArea: Focusarea | Focusareaoption
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
}

const getSectionHeadingTitle = (
	chosenList: PrescriptionsList,
	firstBranchKind: VariantBranch['kind'],
): string => {
	const baseTitle =
		firstBranchKind === 'electives'
			? chosenList.elements.title_ext1.value
			: firstBranchKind === 'author-study'
			? chosenList.elements.title_ext2.value
			: chosenList.elements.title.value
	const years = chosenList.elements.years.value
	return years ? `${baseTitle} (${years})` : baseTitle
}

export default function PrescriptionSection({
	currentFocusArea,
	syllabus,
	linkedItems,
	mappings,
}: PrescriptionSectionProps) {
	const headingId = useId()
	const headingRef = useRef<HTMLDivElement>(null)

	const websiteConfig = useWebsiteConfigElements()

	const isViewSideBySideSupported = useSideBySideSupport()
	const { isInIframe } = useKontentHomeConfig()
	const iframeSide = useIframeSide()

	const resolved = resolveList(currentFocusArea, linkedItems)

	const focusAreaCodename = currentFocusArea.system.codename
	const chosenListId = resolved?.chosenList.system.id ?? null
	useEffect(() => {
		if (!chosenListId || !resolved) return
		sendPrescriptionGAEvent({
			event: PRESCRIPTION_GA_EVENT.listView,
			...getGAPageContext(),
			focus_area: currentFocusArea.elements.title.value,
			syllabus: getSyllabusTitleWithPublicationYear(syllabus),
			prescriptions_list: `${resolved.chosenList.elements.title.value} (${resolved.chosenList.elements.years.value})`,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusAreaCodename, chosenListId])

	const getGAEventBase = useCallback(() => {
		const list = resolved?.chosenList
		return {
			focus_area: currentFocusArea.elements.title.value,
			syllabus: getSyllabusTitleWithPublicationYear(syllabus),
			prescriptions_list: list
				? `${list.elements.title.value} (${list.elements.years.value})`
				: '',
		}
	}, [currentFocusArea, syllabus, resolved])

	if (!resolved) return null

	const { chosenList, filteredContainers } = resolved
	const branches = dispatchVariant(filteredContainers, linkedItems)

	if (branches.length === 0) return null

	const firstBranchKind = branches[0].kind
	const headingTitle = getSectionHeadingTitle(chosenList, firstBranchKind)

	const showStatusAlert = !isRichtextElementEmpty(
		chosenList.elements.status_message,
	)

	const showFootnote = hasCollectionsTrigger(filteredContainers, linkedItems)
	const collectionsRichText =
		websiteConfig.elements?.message_prescriptions_collections

	const footnoteEl =
		showFootnote && collectionsRichText ? (
			<div
				data-testid="prescription-collections-footnote"
				className="mt-8"
			>
				<RichText
					richTextElement={collectionsRichText}
					linkedItems={websiteConfig.linkedItems}
					mappings={mappings}
				/>
			</div>
		) : null

	const lastElectivesBranchIndex = branches.reduce(
		(last, b, i) => (b.kind === 'electives' ? i : last),
		-1,
	)

	const syllabusCodename = syllabus.elements.syllabus.value[0]?.codename ?? ''
	const focusAreaCourseCodename = isFocusarea(currentFocusArea)
		? currentFocusArea.elements.course?.value?.[0] ?? ''
		: ''
	const courseParam = focusAreaCourseCodename || syllabusCodename
	const selectionToolPath =
		getUrlFromMapping(mappings, PRESCRIPTION_TOOLS_CODENAME) ||
		PRESCRIPTION_TOOLS_FALLBACK_PATH
	const innerSelectionToolUrl = `${selectionToolPath}?list=${encodeURIComponent(
		chosenList.system.codename,
	)}&course=${encodeURIComponent(courseParam)}`

	const openSelectionToolSideBySide = () => {
		// Inside the view-side-by-side iframe → load the tool into the OTHER panel.
		if (isInIframe) {
			if (iframeSide) {
				window.parent.postMessage(
					{
						type: `${
							iframeSide === 'left' ? 'right' : 'left'
						}UrlChange`,
						url: innerSelectionToolUrl,
					},
					'*',
				)
			}
			return
		}
		// Otherwise open side-by-side with the current focus-area page on the
		// left and the selection tool on the right.
		const leftUrl =
			window.location.pathname +
			window.location.search +
			window.location.hash
		window.location.href = `/view-side-by-side?l=${encodeURIComponent(
			leftUrl,
		)}&r=${encodeURIComponent(innerSelectionToolUrl)}`
	}

	const sectionAnchorId = `prescriptions-${chosenList.system.codename}`
	const sectionCopyUrl =
		typeof window !== 'undefined'
			? `${window.location.pathname}#${sectionAnchorId}`
			: `#${sectionAnchorId}`

	return (
		<section
			id={sectionAnchorId}
			data-testid="prescription-section"
			data-kontent-item-id={chosenList.system.id}
			aria-labelledby={headingId}
			className="relative pt-4 lg:pt-0"
		>
			<OutcomeDetailCardTitle
				ref={headingRef}
				variant="brand-dark"
				cssVariableForHeight="--prescription-title-height"
				useInViewOptions={{ rootMargin: '-32px 0px 0px 0px' }}
				className="!border-t-transparent"
			>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
					<CommonCopyUrlWrapper url={sectionCopyUrl}>
						<span
							id={headingId}
							data-testid="prescription-section-heading"
						>
							{headingTitle}
						</span>
					</CommonCopyUrlWrapper>
					<div className="inline-flex items-center gap-2">
						<a
							href={innerSelectionToolUrl}
							data-testid="prescription-selection-tool-link"
							className="inline-flex items-center text-nsw-brand-dark text-base font-normal"
						>
							View in selection tool
						</a>
						{isViewSideBySideSupported && (
							<>
								<div className="w-[1px] h-[24px] bg-gray-400 box-content" />
								<button
									type="button"
									onClick={openSelectionToolSideBySide}
									aria-label="Open in View side-by-side"
									data-testid="prescription-selection-tool-sidebyside"
									className="inline-flex items-center text-nsw-brand-dark"
								>
									<svg
										aria-hidden="true"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H9V6H4V18H9V20H4ZM11 22V2H13V4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H13V22H11ZM13 18H20V6H13V18Z"
											fill="currentColor"
										/>
									</svg>
								</button>
							</>
						)}
					</div>
				</div>
			</OutcomeDetailCardTitle>

			{showStatusAlert && (
				<PrescriptionInPageAlert
					as="warning"
					data-testid="prescription-status-alert"
				>
					<RichText
						richTextElement={chosenList.elements.status_message}
						linkedItems={linkedItems}
						mappings={mappings}
					/>
				</PrescriptionInPageAlert>
			)}

			<div className="mt-6 space-y-6">
				{branches.map((branch, branchIndex) => {
					if (branch.kind === 'standard') {
						return (
							<div
								key={`standard-${branchIndex}`}
								className="space-y-6"
							>
								{branch.containers.map(
									(group: PrescriptionGroupType) => (
										<PrescriptionGroup
											key={group.system.id}
											group={group}
											linkedItems={linkedItems}
											mappings={mappings}
										/>
									),
								)}
							</div>
						)
					}
					// 'electives' or 'author-study'
					return (
						<PrescriptionOption
							key={`${branch.kind}-${branchIndex}`}
							options={branch.containers}
							variantKind={branch.kind}
							chosenListCodename={chosenList.system.codename}
							linkedItems={linkedItems}
							mappings={mappings}
							sectionHeadingRef={headingRef}
							getGAEventBase={getGAEventBase}
							slotBeforePrevNext={
								branchIndex === lastElectivesBranchIndex
									? footnoteEl
									: undefined
							}
						/>
					)
				})}
			</div>

			{lastElectivesBranchIndex < 0 && footnoteEl}
		</section>
	)
}
